/**
 * Deterministic loot generation (M7-B / M7-D2).
 *
 * Pure domain — uses SeededRng only. No Math.random / Date for outcomes.
 * Affix magnitudes persist on instances and are applied in combat via
 * equipmentCombat → characterStats. Numerical tables remain DEV_PROVISIONAL
 * until final M7-D2 numerical approval (see docs/M7D2_STAGE_B_NUMERICAL_GATE.md).
 */

import {
  getItemDef,
  listLootableDefs,
  type ItemCategory,
  type ItemDef,
} from '../data/equipment';
import {
  AFFIX_COUNT_BY_RARITY,
  AFFIX_DEFS,
  CATEGORY_WEIGHTS,
  asItemRarity,
  getAffixDef,
  isLootRarity,
  type AffixRoll,
  type LootRarity,
} from '../data/lootTables';
import {
  AFFIX_COUNT_BY_RARITY_V2,
  BOSS_CHEST_COUNT_BY_MUTATION,
  EQUIPMENT_DROP_CHANCE_BY_MUTATION,
  ITEM_LEVEL_INITIAL_CAP,
  itemLevelBand,
  migrateLegacyDifficulty,
  rarityWeightsForProgression,
  scaleAffixMagnitude,
  type DungeonMutationId,
} from '../data/dungeonProgression';
import { createItemInstance, type ItemInstance } from './equipment';
import type { SeededRng } from './rng';

export type LootSourceKind = 'elite_drop' | 'boss_kill' | 'boss_chest';

export interface GeneratedLootItem extends ItemInstance {
  affixes: AffixRoll[];
  itemLevel: number;
  lootSource: LootSourceKind;
}

export interface GenerateItemOptions {
  source: LootSourceKind;
  /** Stable prefix for instanceId (encounter id, chest id, etc.). */
  idSalt: string;
  dungeonLevel?: number;
  mutation?: DungeonMutationId;
  /** Optional forced rarity (tests). */
  forceRarity?: LootRarity;
  /** Optional forced category (tests). */
  forceCategory?: ItemCategory;
  /** @deprecated Prefer dungeonLevel + mutation. */
  difficulty?: string;
  /** Override IL cap for sims (Infinity = uncapped). */
  itemLevelCap?: number;
}

function pickWeighted<T>(
  rng: SeededRng,
  entries: ReadonlyArray<{ weight: number } & T>,
): T {
  const total = entries.reduce((sum, e) => sum + Math.max(0, e.weight), 0);
  if (total <= 0 || entries.length === 0) {
    throw new Error('pickWeighted: empty or zero-weight table');
  }
  let roll = rng.next() * total;
  for (const entry of entries) {
    roll -= Math.max(0, entry.weight);
    if (roll < 0) {
      return entry;
    }
  }
  return entries[entries.length - 1]!;
}

function nextInstanceId(rng: SeededRng, salt: string): string {
  const a = rng.nextInt(0, 0xffffff).toString(36);
  const b = rng.nextInt(0, 0xffffff).toString(36);
  return `loot_${salt}_${a}${b}`;
}

function resolveProgression(options: GenerateItemOptions): {
  dungeonLevel: number;
  mutation: DungeonMutationId;
} {
  if (options.dungeonLevel != null && options.mutation) {
    return {
      dungeonLevel: options.dungeonLevel,
      mutation: options.mutation,
    };
  }
  return migrateLegacyDifficulty(options.difficulty);
}

export function rollRarity(
  rng: SeededRng,
  dungeonLevelOrDifficulty: number | string,
  mutation: DungeonMutationId = 'standard',
): LootRarity {
  if (typeof dungeonLevelOrDifficulty === 'string') {
    const migrated = migrateLegacyDifficulty(dungeonLevelOrDifficulty);
    return rollRarity(rng, migrated.dungeonLevel, migrated.mutation);
  }
  const table = rarityWeightsForProgression(dungeonLevelOrDifficulty, mutation);
  return pickWeighted(rng, table).rarity;
}

export function rollCategory(rng: SeededRng): ItemCategory {
  return pickWeighted(rng, CATEGORY_WEIGHTS).category;
}

export function rollBaseItemDef(
  rng: SeededRng,
  category: ItemCategory,
): ItemDef {
  const pool = listLootableDefs(category);
  if (pool.length === 0) {
    throw new Error(`No lootable defs for category ${category}`);
  }
  const idx = rng.nextInt(0, pool.length - 1);
  return pool[idx]!;
}

export function rollItemLevel(
  rng: SeededRng,
  dungeonLevel: number,
  mutation: DungeonMutationId,
  cap: number = ITEM_LEVEL_INITIAL_CAP,
): number {
  const band = itemLevelBand(dungeonLevel, mutation, cap);
  return rng.nextInt(band.min, band.max);
}

/**
 * Roll unique affixes for a rarity/category. No duplicate affix ids.
 * Magnitudes scaled by rarity mult (DEV_PROVISIONAL).
 */
export function rollAffixes(
  rng: SeededRng,
  rarity: LootRarity,
  category: ItemCategory,
): AffixRoll[] {
  const count =
    AFFIX_COUNT_BY_RARITY_V2[rarity] ?? AFFIX_COUNT_BY_RARITY[rarity] ?? 0;
  if (count <= 0) return [];

  const eligible = AFFIX_DEFS.filter((a) => a.categories.includes(category));
  if (eligible.length === 0) return [];

  const pool = [...eligible];
  const rolls: AffixRoll[] = [];
  const take = Math.min(count, pool.length);
  for (let i = 0; i < take; i += 1) {
    const idx = rng.nextInt(0, pool.length - 1);
    const [picked] = pool.splice(idx, 1);
    if (!picked) break;
    const raw = rng.nextInt(picked.valueMin, picked.valueMax);
    const value = scaleAffixMagnitude(
      raw,
      rarity,
      picked.valueMin,
      picked.valueMax,
    );
    rolls.push({ affixId: picked.id, value });
  }
  return rolls;
}

export function validateAffixRolls(
  rolls: readonly AffixRoll[],
  category: ItemCategory,
): { ok: true } | { ok: false; reason: string } {
  const seen = new Set<string>();
  for (const roll of rolls) {
    if (seen.has(roll.affixId)) {
      return { ok: false, reason: `duplicate affix ${roll.affixId}` };
    }
    seen.add(roll.affixId);
    const def = getAffixDef(roll.affixId);
    if (!def) {
      return { ok: false, reason: `unknown affix ${roll.affixId}` };
    }
    if (!def.categories.includes(category)) {
      return {
        ok: false,
        reason: `affix ${roll.affixId} incompatible with ${category}`,
      };
    }
    const expandedMax = Math.max(
      def.valueMax,
      Math.floor(def.valueMax * 1.3),
    );
    if (
      !Number.isInteger(roll.value) ||
      roll.value < def.valueMin ||
      roll.value > expandedMax
    ) {
      return {
        ok: false,
        reason: `affix ${roll.affixId} value ${roll.value} out of range`,
      };
    }
  }
  return { ok: true };
}

/** Generate one equipment item via the loot pipeline. */
export function generateLootItem(
  rng: SeededRng,
  options: GenerateItemOptions,
): GeneratedLootItem {
  const { dungeonLevel, mutation } = resolveProgression(options);
  const rarity = options.forceRarity ?? rollRarity(rng, dungeonLevel, mutation);
  if (!isLootRarity(rarity)) {
    throw new Error(`Invalid loot rarity: ${rarity}`);
  }
  const category = options.forceCategory ?? rollCategory(rng);
  const def = rollBaseItemDef(rng, category);
  const itemLevel = rollItemLevel(
    rng,
    dungeonLevel,
    mutation,
    options.itemLevelCap ?? ITEM_LEVEL_INITIAL_CAP,
  );
  const affixes = rollAffixes(rng, rarity, def.category);
  const check = validateAffixRolls(affixes, def.category);
  if (!check.ok) {
    throw new Error(`Affix validation failed: ${check.reason}`);
  }

  const instanceId = nextInstanceId(rng, options.idSalt);
  const base = createItemInstance(def.id, {
    instanceId,
    rarity: asItemRarity(rarity),
    acquiredAt: 0,
    source: options.source,
  });

  return {
    ...base,
    affixes,
    itemLevel,
    lootSource: options.source,
  };
}

export function bossChestItemCount(mutation: DungeonMutationId): number {
  return BOSS_CHEST_COUNT_BY_MUTATION[mutation];
}

/** Chance roll for elite/boss-kill equipment (mutation-aware). */
export function rollEquipmentDropChance(
  rng: SeededRng,
  kind: 'elite' | 'bossKill',
  mutation: DungeonMutationId = 'standard',
): boolean {
  const chances = EQUIPMENT_DROP_CHANCE_BY_MUTATION[mutation];
  const chance = kind === 'elite' ? chances.elite : chances.bossKill;
  return rng.next() < chance;
}

export function generateBossChestLoot(
  rng: SeededRng,
  dungeonLevelOrDifficulty: number | string,
  mutationOrEncounterId: DungeonMutationId | string,
  encounterIdMaybe?: string,
  itemLevelCap?: number,
): GeneratedLootItem[] {
  // Legacy: (rng, difficulty, encounterId)
  let dungeonLevel: number;
  let mutation: DungeonMutationId;
  let encounterId: string;
  if (typeof dungeonLevelOrDifficulty === 'string') {
    const migrated = migrateLegacyDifficulty(dungeonLevelOrDifficulty);
    dungeonLevel = migrated.dungeonLevel;
    mutation = migrated.mutation;
    encounterId = String(mutationOrEncounterId);
  } else {
    dungeonLevel = dungeonLevelOrDifficulty;
    mutation = mutationOrEncounterId as DungeonMutationId;
    encounterId = encounterIdMaybe ?? 'chest';
  }
  const count = bossChestItemCount(mutation);
  const items: GeneratedLootItem[] = [];
  for (let i = 0; i < count; i += 1) {
    items.push(
      generateLootItem(rng, {
        dungeonLevel,
        mutation,
        source: 'boss_chest',
        idSalt: `${encounterId}_chest_${i}`,
        itemLevelCap,
      }),
    );
  }
  return items;
}

export function toItemInstance(
  generated: GeneratedLootItem,
  acquiredAt: number,
): ItemInstance {
  return {
    instanceId: generated.instanceId,
    defId: generated.defId,
    rarity: generated.rarity,
    acquiredAt,
    source: generated.source ?? generated.lootSource,
    affixes: generated.affixes.map((a) => ({ ...a })),
    itemLevel: generated.itemLevel,
  };
}

export function describeLootItem(item: ItemInstance | GeneratedLootItem): string {
  const def = getItemDef(item.defId);
  const name = def?.name ?? item.defId;
  const rarity = String(item.rarity);
  const level = item.itemLevel != null ? ` iLvl ${item.itemLevel}` : '';
  return `${rarity} ${name}${level}`;
}
